import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import LandingNav from '@/components/landing/LandingNav';
import LandingFooter from '@/components/landing/LandingFooter';
import PageMeta from '@/components/landing/PageMeta';
import ApexHeroLogo from '@/components/landing/ApexHeroLogo';

const TYPED_TARGET = '11:14 UTC';

function TypedTimestamp() {
  const [typed, setTyped] = useState('');
  const [caretOn, setCaretOn] = useState(true);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced) { setTyped(TYPED_TARGET); return; }
    }
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(TYPED_TARGET.slice(0, i));
      if (i >= TYPED_TARGET.length) clearInterval(id);
    }, 90);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const blink = setInterval(() => setCaretOn((v) => !v), 520);
    return () => clearInterval(blink);
  }, []);

  return (
    <span
      data-testid="why-typed-timestamp"
      style={{
        fontFamily: 'JetBrains Mono, monospace',
        color: '#0D9488',
        background: 'rgba(13,148,136,0.10)',
        padding: '0 8px',
        borderRadius: 4,
        fontSize: '0.85em',
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {typed}
      <span style={{
        opacity: caretOn ? 1 : 0,
        marginLeft: 2,
        transition: 'opacity 0.05s',
      }}>▍</span>
    </span>
  );
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] },
});

const FORCING = [
  ['EU AI Act — August 2, 2026',
    'High-risk AI systems require technical documentation, continuous logging, human oversight evidence, and audit trail documentation under Articles 12 and 14. Most organizations are not ready.',
    true],
  ['SOC 2 — AI Controls Now Active',
    'SOC 2 auditors are probing AI-related controls through existing Trust Service Criteria lenses. PI1.1, PI1.2, CC6.1. Evidence organizations currently cannot produce.',
    false],
  ['Cyber Insurance Renewal',
    'AI governance documentation is appearing in underwriting questionnaires. Organizations that cannot demonstrate structured AI risk management are encountering harder coverage terms and higher premiums.',
    false],
  ['NIST AI RMF — Public Sector',
    'Increasingly referenced in federal and public-sector procurement. AI governance alignment is becoming a practical contract requirement, not a voluntary framework.',
    false],
];

const AI_COMPETITION = [
  {
    title: 'GRC Platforms',
    sub: 'ServiceNow · OneTrust · Archer',
    bullets: [
      'Govern at the policy level',
      'Produce policy documents and risk registers',
      'Cannot produce per-decision evidence',
      'Cannot reconstruct historical governance state',
    ],
  },
  {
    title: 'AI Security',
    sub: 'Zenity · Lakera · Protect AI',
    bullets: [
      'Protect at the model and agent level',
      'Detect threats and prompt injection',
      'Cannot produce governance evidence',
      'Not designed for audit and regulatory review',
    ],
  },
  {
    title: 'Observability',
    sub: 'Arize · Evidently · Fiddler',
    bullets: [
      'Monitor at the performance level',
      'Track drift, accuracy, and latency',
      'Cannot satisfy regulatory evidence requirements',
      'Not designed for audit-survivable documentation',
    ],
  },
];

const CLOUD_COMPETITION = [
  {
    title: 'CSPM Tools',
    sub: 'Wiz · Orca · Prisma Cloud',
    bullets: [
      'Identify current misconfigurations',
      'Surface findings against benchmarks',
      'Cannot reconstruct historical governance state',
      'Cannot answer “what ran 184 days ago”',
    ],
  },
  {
    title: 'SIEM Platforms',
    sub: 'Splunk · Sumo Logic · Sentinel',
    bullets: [
      'Aggregate logs at the event level',
      'Detect anomalies and active threats',
      'Cannot produce signed governance evidence',
      'Cannot bind events to governance state',
    ],
  },
  {
    title: 'Compliance Scanners',
    sub: 'Vanta · Drata · Secureframe',
    bullets: [
      'Automate checklist compliance evidence',
      'Track control attestation status',
      'Cannot reconstruct point-in-time cloud state',
      'Cannot produce per-event proof objects',
    ],
  },
];

function CompetitionGrid({ heading, items }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 18 }}>
        {heading}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
        {items.map((col) => (
          <div key={col.title} style={{ background: '#0F1629', border: '1px solid #1E2A45', borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#F8FAFC' }}>{col.title}</div>
            <div style={{ fontSize: 10, color: '#475569', fontFamily: 'JetBrains Mono, monospace', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {col.sub}
            </div>
            <ul style={{ marginTop: 14, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {col.bullets.map((b) => (
                <li key={b} style={{ fontSize: 12, color: '#94A3B8', lineHeight: 1.6 }}>▸ {b}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WhyApex() {
  return (
    <div style={{ background: '#06070A', minHeight: '100vh', color: '#F1F5F9' }}>
      <PageMeta
        title="Why Apex — Governance reconstruction for the audits that are coming"
        description="What governed that AI decision? Which controls were active at that exact moment? Posture is not proof. Current state is not historical truth. Apex answers the questions regulators are about to ask."
        path="/why-apex"
      />
      <LandingNav />

      {/* Atmospheric brand mark — small, no wordmark/tagline (page headline carries the message) */}
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120, paddingBottom: 16, opacity: 0.85, pointerEvents: 'none' }}>
        <ApexHeroLogo width={260} showWordmark={false} showTagline={false} />
      </div>

      {/* Section 1 — Two unanswered questions */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '60px 24px 80px', textAlign: 'left' }}>
        <motion.div {...fadeUp(0)}>
          <p style={{
            fontSize: 'clamp(24px, 3.5vw, 36px)',
            color: '#F8FAFC',
            fontWeight: 700,
            lineHeight: 1.35,
            letterSpacing: '-0.02em',
            maxWidth: 800,
            marginTop: 18,
          }}>
            “What governed that AI decision at <TypedTimestamp />?<br />
            Which controls were active at that exact moment?<br />
            Can you prove it — with evidence an auditor can test?”
          </p>
          <p style={{
            marginTop: 24,
            color: '#64748B',
            fontSize: 14,
            fontStyle: 'italic',
            lineHeight: 1.7,
          }}>
            Most organizations cannot answer this. They have logs. Dashboards. Policy documents.<br />
            None of them constitute proof.
          </p>
          <p style={{
            marginTop: 20,
            color: '#475569',
            fontSize: 13,
            lineHeight: 1.7,
          }}>
            Posture is not proof. Current state is not historical truth.<br />
            Most organizations cannot explain what governed a critical AI decision after the fact.
          </p>
        </motion.div>

        <motion.div {...fadeUp(0.15)} style={{ marginTop: 56, paddingTop: 36, borderTop: '1px solid #1E2A45' }}>
          <p style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 10,
            color: '#334155',
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            marginBottom: 20,
          }} data-testid="why-transition">
            The same problem. Different domain.
          </p>
          <p style={{
            fontSize: 'clamp(24px, 3.5vw, 36px)',
            color: '#F8FAFC',
            fontWeight: 700,
            lineHeight: 1.35,
            letterSpacing: '-0.02em',
            maxWidth: 800,
          }}>
            “Which container image ran in your production payments environment 184 days ago?<br />
            Was it approved? What did it access while it was there?”
          </p>
          <p style={{
            marginTop: 24,
            color: '#64748B',
            fontSize: 14,
            fontStyle: 'italic',
            lineHeight: 1.7,
          }}>
            Most organizations cannot reconstruct this either.<br />
            Security Hub has 127,000 findings. None of them answer that question.
          </p>
          <p style={{
            marginTop: 20,
            color: '#475569',
            fontSize: 13,
            lineHeight: 1.7,
          }}>
            When regulators ask what was true then,<br />
            screenshots are not enough.
          </p>
        </motion.div>
      </section>

      {/* Section 2 — Four Forcing Functions */}
      <section style={{ background: '#0F1629', borderTop: '1px solid #1E2A45', borderBottom: '1px solid #1E2A45', padding: '80px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.h2 {...fadeUp(0)} style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 24 }}>
            Four Forcing Functions
          </motion.h2>
          <motion.p {...fadeUp(0.05)} style={{
            color: '#64748B',
            fontSize: 13,
            fontStyle: 'italic',
            marginBottom: 32,
            lineHeight: 1.7,
            maxWidth: 640,
          }}>
            The governance model built for static systems is collapsing under AI.<br />
            These four forces are not warnings. They are deadlines.
          </motion.p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {FORCING.map(([title, body, accent], i) => (
              <motion.div key={title} {...fadeUp(0.08 * i)} style={{
                background: '#0A0A0F', border: '1px solid #1E2A45', borderRadius: 12, padding: 22,
              }}>
                <div style={{ fontSize: 11, color: accent ? '#0D9488' : '#475569', textTransform: 'uppercase', letterSpacing: '0.14em', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
                  {title}
                </div>
                <p style={{ color: '#94A3B8', fontSize: 13, marginTop: 12, lineHeight: 1.7 }}>{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3 — The Competitive Gap */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 24px' }}>
        <motion.div {...fadeUp(0)}>
          <p style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 24 }}>
            Where the Market Stands Today
          </p>
        </motion.div>

        <CompetitionGrid heading="AI Governance Landscape" items={AI_COMPETITION} />

        <div style={{ marginTop: 48 }}>
          <CompetitionGrid heading="Cloud Governance Landscape" items={CLOUD_COMPETITION} />
        </div>

        <motion.div {...fadeUp(0)} style={{
          marginTop: 64, paddingTop: 36, borderTop: '1px solid #1E2A45',
          textAlign: 'left',
        }}>
          <p style={{
            fontSize: 'clamp(20px, 2.8vw, 26px)',
            color: '#F8FAFC',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            lineHeight: 1.5,
            maxWidth: 720,
            marginTop: 0,
            marginBottom: 28,
          }}>
            “Existing governance tools monitor posture.<br />
            Apex reconstructs governance truth over time and proves it.”
          </p>
          <p style={{ fontSize: 'clamp(20px, 2.6vw, 26px)', color: '#F8FAFC', lineHeight: 1.5, marginTop: 16, fontWeight: 600, letterSpacing: '-0.01em', maxWidth: 820 }}>
            Per-decision, cryptographically signed, framework-mapped evidence objects that auditors can sample and test —
            generated automatically from existing AI activity, reconstructable at any point in time.
          </p>
          <p style={{ color: '#94A3B8', fontSize: 14, marginTop: 16 }}>
            That is what Apex provides.
          </p>
        </motion.div>
      </section>

      {/* CTA */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px 120px', display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Link to="/platform" style={{ background: '#0D9488', color: 'white', padding: '12px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
          See how Apex fills it →
        </Link>
        <Link to="/product/lens" style={{ border: '1px solid #0D9488', color: '#0D9488', padding: '12px 22px', borderRadius: 10, fontSize: 13, textDecoration: 'none' }}>
          See Lens →
        </Link>
        <Link to="/product/trust" style={{ border: '1px solid #6366F1', color: '#818CF8', padding: '12px 22px', borderRadius: 10, fontSize: 13, textDecoration: 'none' }}>
          See Trust →
        </Link>
      </section>
      <LandingFooter />
    </div>
  );
}
